import React, { useCallback, useEffect, useMemo, useState } from "react";
import OrdersIcon from "@mui/icons-material/AssignmentOutlined";
import CompleteIcon from "@mui/icons-material/CheckCircleOutline";
import PendingIcon from "@mui/icons-material/HourglassEmpty";
import PrintIcon from "@mui/icons-material/LocalPrintshopOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import AdvanceIcon from "@mui/icons-material/ArrowForward";
import {
  Alert,
  Box,
  Button,
  debounce,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CustomeDataGrid, Chip as UiChip, TextField } from "../../components/ui";
import { showErrorToast, showSuccessToast } from "../../components/common/ShowToast";
import apiService from "../../service/Api";
import type { RootState } from "../../store/Index";
import { tokens } from "../../theme";

export type ProgramOrderStatus =
  | "new"
  | "in_review"
  | "proof_sent"
  | "approved"
  | "printing"
  | "dispatched"
  | "completed"
  | "cancelled";

export type ProgramOrderAddOn = {
  key: string;
  label: string;
  price: number;
};

export type ProgramOrderSection = { title: string; detail: string };
export type ProgramOrderHymn = { title: string; performer: string; lyrics: string };

export type ProgramOrder = {
  id: number;
  orderNumber: string;
  clientName: string;
  email: string;
  phone: string;
  deceasedName: string;
  programTitle: string;
  programSubtitle: string;
  birthDate: string | null;
  passingDate: string | null;
  venue: string;
  serviceTime: string;
  obituary: string;
  pallbearers: string[];
  acknowledgement: string;
  template: string;
  serviceDate: string | null;
  copies: number;
  pages: number;
  paper: string;
  // Add-ons selected at order time, with their price *as charged* --
  // snapshotted here rather than re-looked-up from the add-on catalog, so a
  // later catalog price change can't retroactively alter past orders.
  addOns: ProgramOrderAddOn[];
  printSubtotal: number | null;
  total: number | null;
  sections: ProgramOrderSection[];
  hymns: ProgramOrderHymn[];
  coverPhoto: string | null;
  ownFile: string | null;
  deliveryAddress: string;
  notes: string;
  status: ProgramOrderStatus;
  submittedAt: string;
};

// Shape returned by ProgramOrderStaffSerializer (backend/booking/serializers.py).
type ApiProgramOrder = {
  id: number;
  client_name: string;
  client_phone: string;
  client_email: string | null;
  contact: number | null;
  contact_image: string | null;
  deceased_name: string;
  program_title: string;
  program_subtitle: string;
  birth_date: string | null;
  passing_date: string | null;
  venue: string;
  service_time: string;
  service_date: string | null;
  obituary: string;
  pallbearers: string[];
  acknowledgement: string;
  template: number | null;
  template_name: string | null;
  own_file: string | null;
  sections: ProgramOrderSection[];
  hymns: ProgramOrderHymn[];
  cover_photo: string | null;
  copies: number;
  pages: number;
  paper_name: string;
  paper_multiplier: string;
  addons_snapshot: { key: string; label: string; price: number | string }[];
  print_subtotal: string | null;
  total: string | null;
  delivery_address: string;
  notes: string;
  status: ProgramOrderStatus;
  created_at: string;
};

type PaginatedResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiProgramOrder[];
};

export function mapApiProgramOrder(api: ApiProgramOrder): ProgramOrder {
  return {
    id: api.id,
    orderNumber: `MZK-PRG-${api.id}`,
    clientName: api.client_name,
    email: api.client_email ?? "",
    phone: api.client_phone,
    deceasedName: api.deceased_name,
    programTitle: api.program_title || `In Loving Memory of ${api.deceased_name}`,
    programSubtitle: api.program_subtitle ?? "",
    birthDate: api.birth_date,
    passingDate: api.passing_date,
    venue: api.venue ?? "",
    serviceTime: api.service_time ?? "",
    obituary: api.obituary ?? "",
    pallbearers: api.pallbearers ?? [],
    acknowledgement: api.acknowledgement ?? "",
    template: api.template_name || "Customer's own uploaded PDF",
    serviceDate: api.service_date,
    copies: api.copies,
    pages: api.pages,
    paper: api.paper_name || "Bond",
    addOns: (api.addons_snapshot ?? []).map((addOn) => ({
      key: addOn.key,
      label: addOn.label,
      price: Number(addOn.price),
    })),
    printSubtotal: api.print_subtotal == null ? null : Number(api.print_subtotal),
    total: api.total == null ? null : Number(api.total),
    sections: api.sections ?? [],
    hymns: api.hymns ?? [],
    coverPhoto: api.cover_photo,
    ownFile: api.own_file,
    deliveryAddress: api.delivery_address,
    notes: api.notes,
    status: api.status,
    submittedAt: api.created_at,
  };
}

const STATUS_OPTIONS: { value: ProgramOrderStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In review" },
  { value: "proof_sent", label: "Proof sent" },
  { value: "approved", label: "Approved" },
  { value: "printing", label: "Printing" },
  { value: "dispatched", label: "Ready for delivery" },
  { value: "completed", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_FLOW: ProgramOrderStatus[] = [
  "new",
  "in_review",
  "proof_sent",
  "approved",
  "printing",
  "dispatched",
  "completed",
];

const IN_PROGRESS_STATUSES: ProgramOrderStatus[] = [
  "in_review",
  "proof_sent",
  "approved",
  "printing",
  "dispatched",
];

const STATUS_PRESENTATION: Record<
  ProgramOrderStatus,
  { label: string; status: "neutral" | "info" | "warning" | "success" | "error" }
> = {
  new: { label: "New", status: "info" },
  in_review: { label: "In review", status: "warning" },
  proof_sent: { label: "Proof sent", status: "info" },
  approved: { label: "Approved", status: "success" },
  printing: { label: "Printing", status: "warning" },
  dispatched: { label: "Ready for delivery", status: "info" },
  completed: { label: "Delivered", status: "success" },
  cancelled: { label: "Cancelled", status: "neutral" },
};

const formatDate = (value: string | null) =>
  value
    ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "Not set";

const ProgramOrdersPage: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const navigate = useNavigate();

  const [orders, setOrders] = useState<ProgramOrder[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProgramOrderStatus | "all">("all");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 300), []);
  useEffect(() => () => debouncedSetSearch.clear(), [debouncedSetSearch]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await apiService.get<PaginatedResponse>(
        "/api/bookings/program-orders/",
        {
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
          search: search || undefined,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
      );
      setOrders((response.data.results ?? []).map(mapApiProgramOrder));
      setRowCount(response.data.count ?? 0);
    } catch (error) {
      console.error("Failed to load program orders", error);
      setLoadError("Program orders could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, search, statusFilter]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [search, statusFilter]);

  const [statusCounts, setStatusCounts] = useState<Record<ProgramOrderStatus, number>>({
    new: 0, in_review: 0, proof_sent: 0, approved: 0, printing: 0, dispatched: 0,
    completed: 0, cancelled: 0,
  });

  // Status counts for the summary cards are independent of the current
  // page/search/status filter, so they're fetched separately (page_size=1
  // keeps the payload tiny -- only the `count` field is used).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          STATUS_OPTIONS.map((option) =>
            apiService.get<PaginatedResponse>("/api/bookings/program-orders/", {
              status: option.value,
              page_size: 1,
            }),
          ),
        );
        if (cancelled) return;
        const next = { ...statusCounts };
        STATUS_OPTIONS.forEach((option, index) => {
          next[option.value] = results[index].data.count ?? 0;
        });
        setStatusCounts(next);
      } catch (error) {
        console.error("Failed to load program order status counts", error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const newCount = statusCounts.new;
  const inProgressCount = IN_PROGRESS_STATUSES.reduce((sum, status) => sum + statusCounts[status], 0);
  const deliveredCount = statusCounts.completed;

  const handleAdvanceStatus = async (
    order: ProgramOrder,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    const currentIndex = STATUS_FLOW.indexOf(order.status);
    if (currentIndex < 0) return;
    const nextStatus = STATUS_FLOW[currentIndex + 1];
    if (!nextStatus) return;

    try {
      await apiService.patch(`/api/bookings/program-orders/${order.id}/`, { status: nextStatus });
      setOrders((current) =>
        current.map((item) => (item.id === order.id ? { ...item, status: nextStatus } : item)),
      );
      showSuccessToast(`${order.orderNumber} moved to ${STATUS_PRESENTATION[nextStatus].label}`);
    } catch {
      showErrorToast("Could not update the order status.");
    }
  };

  const columns: GridColDef<ProgramOrder>[] = [
    {
      field: "orderNumber",
      headerName: "ORDER",
      minWidth: 140,
      flex: 0.9,
      renderCell: (params: GridRenderCellParams<ProgramOrder>) => (
        <Box sx={{ py: 1 }}>
          <Typography fontWeight={700} variant="body2" sx={{ color: colors.primary[400] }}>
            {params.row.orderNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(params.row.submittedAt).toLocaleDateString("en-GB")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "clientName",
      headerName: "CLIENT",
      minWidth: 180,
      flex: 1.1,
      renderCell: (params: GridRenderCellParams<ProgramOrder>) => (
        <Box sx={{ py: 1 }}>
          <Typography fontWeight={600} variant="body2">
            {params.row.clientName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      field: "deceasedName",
      headerName: "MEMORIAL FOR",
      minWidth: 180,
      flex: 1.1,
      renderCell: (params: GridRenderCellParams<ProgramOrder>) => (
        <Box sx={{ py: 1 }}>
          <Typography fontWeight={600} variant="body2">
            {params.row.deceasedName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.template}
          </Typography>
        </Box>
      ),
    },
    {
      field: "serviceDate",
      headerName: "SERVICE DATE",
      minWidth: 130,
      flex: 0.8,
      valueGetter: (_value, row) => formatDate(row.serviceDate),
    },
    {
      field: "specification",
      headerName: "PROGRAM SPEC",
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) => `${row.pages} pages · ${row.paper} · ${row.copies} pcs`,
    },
    {
      field: "total",
      headerName: "TOTAL",
      width: 110,
      renderCell: (params: GridRenderCellParams<ProgramOrder>) => (
        <Typography fontWeight={700} variant="body2" sx={{ color: colors.primary[400] }}>
          {params.row.total == null ? "To confirm" : `$${params.row.total.toFixed(2)}`}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "STATUS",
      minWidth: 130,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<ProgramOrder>) => {
        const presentation = STATUS_PRESENTATION[params.row.status];
        return <UiChip label={presentation.label} status={presentation.status} />;
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ProgramOrder>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {STATUS_FLOW.indexOf(params.row.status) >= 0 &&
            STATUS_FLOW.indexOf(params.row.status) < STATUS_FLOW.length - 1 && (
            <Button
              size="small"
              title={`Advance to ${STATUS_PRESENTATION[STATUS_FLOW[STATUS_FLOW.indexOf(params.row.status) + 1]].label}`}
              aria-label={`Advance ${params.row.orderNumber} to next status`}
              onClick={(event) => void handleAdvanceStatus(params.row, event)}
              sx={{ minWidth: 0, p: 0.75, color: colors.primary[400] }}
            >
              <AdvanceIcon sx={{ fontSize: 18 }} />
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color={colors.primary[100]}>
            Program Orders
          </Typography>
          <Typography color="text.secondary" mt={0.5} variant="body2">
            Review customer memorial program submissions, proofs, and print fulfillment.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            onClick={() => navigate("/configurations/program-builder-templates")}
            startIcon={<TuneIcon />}
            sx={{
              borderRadius: "999px",
              bgcolor: "transparent",
              color: colors.primary[400],
              fontWeight: 600,
              textTransform: "none",
              fontSize: 13,
              px: 2,
              "&:hover": { bgcolor: alpha(colors.primary[400], 0.08) },
            }}
          >
            Configure templates
          </Button>
          <UiChip icon={<OrdersIcon />} label={`${rowCount} orders`} status="neutral" />
        </Stack>
      </Stack>

      {/* 3 Simple KPI Cards (borderless, matching view-booking style) */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard label="New submissions" value={newCount} icon={<PendingIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard label="In production" value={inProgressCount} icon={<PrintIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SummaryCard label="Delivered" value={deliveredCount} icon={<CompleteIcon />} />
        </Grid>
      </Grid>

      {/* Main Table Container (borderless Paper) */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "14px",
          boxShadow: 0,
          bgcolor: colors.primary[900],
        }}
      >
        {/* Search & Filter */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2.5}>
          <TextField
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              debouncedSetSearch(event.target.value);
            }}
            placeholder="Search order ID, client, or loved one"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <InputLabel id="program-order-status-label">Status</InputLabel>
            <Select
              labelId="program-order-status-label"
              value={statusFilter}
              label="Status"
              onChange={(event) => setStatusFilter(event.target.value as ProgramOrderStatus | "all")}
            >
              <MenuItem value="all">All statuses</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void loadOrders()}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {/* Data Grid */}
        <CustomeDataGrid
          rows={orders}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) =>
            navigate(`/program-orders/${params.row.id}`, {
              state: { order: params.row as ProgramOrder },
            })
          }
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowHeight={() => "auto"}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": {
              borderBottomColor: alpha(theme.palette.text.primary, 0.04),
            },
            "& .MuiDataGrid-columnHeaders": { borderBottom: "none" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
          localeText={{
            noRowsLabel: loading ? "Loading program orders…" : "No program orders match your filters.",
          }}
        />
      </Paper>
    </Box>
  );
};

const SummaryCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        minHeight: 88,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: "14px",
        boxShadow: 0,
        bgcolor: colors.primary[900],
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          bgcolor: `${colors.primary[400]}20`,
          color: colors.primary[400],
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

export default ProgramOrdersPage;
